import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import { categoryMetas } from "@/mocks/articles";

const values = [
  {
    icon: "ri-compasses-2-line",
    title: "深入而非浮泛",
    desc: "我们相信好的内容值得慢慢写、慢慢读。每篇文章都力求提供有据可循的分析，而不是匆匆的结论。",
  },
  {
    icon: "ri-links-line",
    title: "连接理论与实践",
    desc: "理论不应悬在云端。我们把概念放回具体的影片与场景中，让阅读成为一场可验证的思考。",
  },
  {
    icon: "ri-open-arm-line",
    title: "开放而友好的讨论",
    desc: "无论你是研究者、学生还是普通影迷，只要对电影保持好奇，这里都欢迎你的阅读与回应。",
  },
];

const team = [
  { name: "沈砚", role: "主编 · 影视理论", desc: "关注蒙太奇理论与电影史，长期从事电影理论研究与写作。" },
  { name: "陈知微", role: "评论主笔", desc: "以细读方式撰写影评，聚焦华语电影与作者导演的风格分析。" },
  { name: "何川", role: "文化与产业观察", desc: "关注电影产业、流媒体转型与电影节生态的长期变化。" },
  { name: "苏晚", role: "特约撰稿", desc: "研究女性主义电影批评、凝视理论与身份议题。" },
];

export default function AboutPage() {
  return (
    <SiteLayout>
      <section className="bg-primary-900 text-background-50">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-14 md:py-20">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <p className="text-xs tracking-[0.3em] text-accent-400 mt-6 mb-4 uppercase">About Us</p>
          <h1 className="font-heading text-3xl md:text-4xl font-bold mb-5">关于理论电影</h1>
          <p className="text-sm md:text-base text-background-200 leading-relaxed max-w-3xl">
            理论电影是一个专注影视理论与电影评论的内容平台。我们以杂志编辑式的编排，呈现成体系的深度文章，
            涵盖电影理论探讨、影片评论、影视文化研究与产业观察，帮助读者在观看之外，获得一种理解电影的方式。
          </p>
        </div>
      </section>

      <section className="max-w-[1280px] mx-auto px-4 md:px-8 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-5">我们的定位</h2>
            <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              <p>
                面向电影爱好者、影视研究人员、电影评论家及高校影视专业学生，理论电影致力于提供内容深度兼顾学术与趣味的影视内容。
                我们既讨论爱森斯坦的辩证蒙太奇，也细读一部具体的影片，努力让理论可感、让评论有据。
              </p>
              <p>
                我们坚持内容的可读性与专业性的平衡：不堆砌术语，也不放弃深度；不追逐流量式的结论，也不回避真正的问题。
                电影是一门值得认真对待的艺术，我们希望这里成为认真对待它的人的一个据点。
              </p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-background-100 rounded-lg p-7">
              <h3 className="font-heading text-lg font-bold text-foreground-950 mb-5">内容栏目</h3>
              <ul className="space-y-4">
                {categoryMetas.map((c) => (
                  <li key={c.id}>
                    <Link to={c.path} className="group flex items-start gap-3">
                      <span className="w-9 h-9 rounded-md bg-background-50 text-secondary-700 flex items-center justify-center shrink-0">
                        <i className={c.icon} />
                      </span>
                      <span>
                        <span className="block text-sm font-semibold text-foreground-900 group-hover:text-secondary-700 transition-colors">
                          {c.name}
                        </span>
                        <span className="block text-xs text-foreground-500 mt-1 leading-relaxed">
                          {c.description}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 border-y border-background-200">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-14 md:py-20">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-10">我们的坚持</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {values.map((v) => (
              <div key={v.title} className="bg-background-50 rounded-lg p-6 border border-background-200">
                <span className="w-12 h-12 rounded-md bg-accent-100 text-accent-800 flex items-center justify-center mb-5">
                  <i className={`${v.icon} text-xl`} />
                </span>
                <h3 className="font-heading text-lg font-bold text-foreground-950 mb-3">{v.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1280px] mx-auto px-4 md:px-8 py-14 md:py-20">
        <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-10">编辑团队</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {team.map((m) => (
            <div key={m.name} className="text-center bg-background-50 border border-background-200 rounded-lg p-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-heading text-2xl font-bold mb-4">
                {m.name.slice(0, 1)}
              </div>
              <h3 className="font-heading text-base font-bold text-foreground-950">{m.name}</h3>
              <p className="text-xs text-secondary-700 mt-1 mb-3">{m.role}</p>
              <p className="text-xs text-foreground-500 leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-[1280px] mx-auto px-4 md:px-8 pb-16">
        <div className="bg-primary-50 border border-primary-100 rounded-lg p-7 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-2">有想法想和我们聊聊？</h2>
            <p className="text-sm text-foreground-600">
              欢迎通过留言表单或官方邮箱与我们联系，我们期待你的反馈与投稿。
            </p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-secondary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-secondary-600 transition-colors"
            >
              <i className="ri-chat-3-line" />
              联系我们
            </Link>
            <a
              href="mailto:contact@theoryfilm.cn"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-background-300 text-sm text-foreground-700 whitespace-nowrap hover:border-secondary-400 hover:text-secondary-700 transition-colors"
            >
              <i className="ri-mail-line" />
              contact@theoryfilm.cn
            </a>
          </div>
        </div>
        <p className="text-xs text-foreground-400 text-center mt-10 leading-relaxed">
          © 2026 理论电影 版权所有。本站内容仅供学习与交流使用，未经许可不得用于商业用途。<br />
          本站为影视理论与电影评论内容平台演示，不提供影片播放或下载服务。
        </p>
      </section>
    </SiteLayout>
  );
}